"use client";
/**
 * SANATAN DECODE — style proof v3. 21s @ 30fps, 1280x720.
 *
 * Yash's notes on v2: needs to be more engaging, and it must FEEL like a video about Sanatan.
 * v3 answers both:
 *   - Every word is anchored to the REAL Deva Katha narration (voice 6EphsklDopDQ6eRkwNHT,
 *     eleven_multilingual_v2, speed 1.15 / stab 0.5 / sim 0.45 — same as audio.py), so text
 *     lands on the spoken syllable instead of on an invented beat grid.
 *   - Sanatan visual identity: sandalwood ground, rotating mandala watermark, temple-frieze
 *     rails, a cusped mandir arch around the shastra rule, diya glow, marigold petal drift,
 *     gold ornament. The lineage line is drawn as a SUTRA (thread), not a chart axis.
 *   - Cadence: 6 beats instead of 4, per-word landings, scale contrast.
 * Illustrations are gpt-image-2 cutouts (white keyed out locally).
 */
import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,

  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
  random,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", {
  weights: ["400", "600", "700", "800", "900"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: inter } = loadInter("normal", {
  weights: ["500", "700", "800"],
  subsets: ["latin"],
});

/* ── palette ──────────────────────────────────────────────────── */
const SAND = "#F3E7CE";
const INK = "#1B2A4A";
const GOLD = "#C0932B";
const MARIGOLD = "#D98324";
const KUMKUM = "#C4553D";
const MUTED = "#8D8672";

const FPS = 30;
const f = (s: number) => s * FPS;

/* Word timings straight from ElevenLabs /with-timestamps (audio.py's _chars_to_words). */
const W: Record<number, [number, number, string]> = {
  0: [0.0, 0.15, "आप"], 1: [0.21, 0.35, "जिस"], 2: [0.41, 0.71, "गोत्र"], 3: [0.74, 0.82, "का"],
  4: [0.87, 1.06, "नाम"], 5: [1.13, 1.36, "लेते"], 6: [1.42, 1.64, "हैं"],
  8: [2.03, 2.16, "वो"], 9: [2.26, 2.47, "आपका"], 10: [2.58, 3.01, "सरनेम"], 11: [3.05, 3.24, "नहीं"],
  12: [3.32, 3.6, "है।"],
  15: [4.35, 4.7, "ऋषि"],
  26: [8.2, 8.42, "एक"], 27: [8.45, 8.52, "ही"], 28: [8.63, 9.17, "रक्त-रेखा"],
  34: [11.61, 12.03, "शास्त्र"],
  38: [13.41, 14.03, "सगोत्र"], 39: [14.41, 14.76, "विवाह"], 40: [14.86, 15.27, "नहीं।"],
  45: [17.43, 17.86, "विज्ञान"],
};

/** spring that fires when a given word is spoken */
const useWordSpring = (startSec: number, damping = 17, stiffness = 160) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - f(startSec), fps, config: { damping, stiffness, mass: 0.7 } });
};

/* ══ SANATAN FURNITURE ═════════════════════════════════════════ */

/** Slowly turning mandala watermark — the single strongest "this is Sanatan" cue. */
const Mandala: React.FC = () => {
  const frame = useCurrentFrame();
  const rot = interpolate(frame, [0, 640], [0, 26]);
  const petal = (i: number, r: number, len: number, w: number) => (
    <path
      key={`${r}-${i}`}
      d={`M0 ${-r} C ${w} ${-r - len * 0.55}, ${w} ${-r - len}, 0 ${-r - len} C ${-w} ${-r - len}, ${-w} ${-r - len * 0.55}, 0 ${-r}`}
      fill="none"
      stroke={GOLD}
      strokeWidth={2}
      transform={`rotate(${i * (360 / 16)})`}
    />
  );
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
      <svg width={1180} height={1180} viewBox="-590 -590 1180 1180" style={{ opacity: 0.22 }}>
        <g transform={`rotate(${rot})`}>
          {Array.from({ length: 16 }, (_, i) => petal(i, 150, 120, 42))}
          {Array.from({ length: 16 }, (_, i) => petal(i, 300, 150, 54))}
          <g transform={`rotate(${360 / 32})`}>
            {Array.from({ length: 16 }, (_, i) => petal(i, 460, 100, 40))}
          </g>
          <circle r={132} fill="none" stroke={GOLD} strokeWidth={2} />
          <circle r={118} fill="none" stroke={GOLD} strokeWidth={1} />
          <circle r={584} fill="none" stroke={GOLD} strokeWidth={2} />
        </g>
      </svg>
    </AbsoluteFill>
  );
};

/** Temple frieze rail — repeating cusped arch + bead, top and bottom. */
const Frieze: React.FC<{ y: number; flip?: boolean }> = ({ y, flip }) => {
  const unit = 44;
  const n = Math.ceil(1280 / unit) + 1;
  return (
    <svg
      width={1280}
      height={26}
      style={{ position: "absolute", left: 0, top: y, transform: flip ? "scaleY(-1)" : undefined, opacity: 0.5 }}
    >
      <line x1={0} y1={22} x2={1280} y2={22} stroke={GOLD} strokeWidth={1.5} />
      {Array.from({ length: n }, (_, i) => (
        <g key={i} transform={`translate(${i * unit},0)`}>
          <path d={`M6 22 Q6 8 ${unit / 2} 8 Q${unit - 6} 8 ${unit - 6} 22`} fill="none" stroke={GOLD} strokeWidth={1.5} />
          <circle cx={unit / 2} cy={4} r={2.6} fill={GOLD} />
        </g>
      ))}
    </svg>
  );
};

/** Warm diya glow, breathing. */
const DiyaGlow: React.FC = () => {
  const frame = useCurrentFrame();
  const p = 0.5 + 0.5 * Math.sin(frame / 26);
  return (
    <AbsoluteFill
      style={{
        pointerEvents: "none",
        background: `radial-gradient(ellipse 62% 58% at 50% 46%, rgba(255,214,140,${0.3 + p * 0.14}) 0%, rgba(255,214,140,0) 68%)`,
      }}
    />
  );
};

/** Marigold petals drifting down. */
const Petals: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 16 }, (_, i) => {
        const seed = `p${i}`;
        const x = random(seed) * 1280;
        const sp = 0.32 + random(seed + "s") * 0.5;
        const y = ((random(seed + "y") * 800 + frame * sp) % 820) - 60;
        const sway = Math.sin((frame + i * 40) / 42) * 22;
        const r = 3 + random(seed + "r") * 4;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x + sway,
              top: y,
              width: r * 2,
              height: r * 1.3,
              borderRadius: "50%",
              background: i % 3 === 0 ? KUMKUM : MARIGOLD,
              opacity: 0.3,
              transform: `rotate(${frame * 0.6 + i * 30}deg)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const Ground: React.FC = () => {
  const frame = useCurrentFrame();
  const s = interpolate(frame, [0, 640], [1.06, 1.13]);
  return (
    <AbsoluteFill style={{ backgroundColor: SAND, overflow: "hidden" }}>
      <Img src={staticFile("sanatan/bg_paper.png")} style={{ width: "100%", height: "100%", objectFit: "cover", transform: `scale(${s})` }} />
      <AbsoluteFill style={{ background: "radial-gradient(ellipse 80% 72% at 50% 46%, rgba(255,250,236,0.5) 0%, rgba(196,150,88,0.30) 100%)" }} />
    </AbsoluteFill>
  );
};

const Furniture: React.FC = () => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <Frieze y={8} />
      <Frieze y={686} flip />
      <div style={{ position: "absolute", top: 44, left: 52, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 18, background: MARIGOLD, borderRadius: 2 }} />
        <span style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, letterSpacing: 3, color: INK, opacity: 0.66 }}>
          सनातन डिकोड
        </span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: MARIGOLD, opacity: 0.9 }} />
    </AbsoluteFill>
  );
};

/* Cutout that lands and then drifts. */
const Cutout: React.FC<{
  src: string; h?: number; w?: number; left?: number; top?: number;
  at: number; drift?: number; rotate?: number;
}> = ({ src, h, w, left = 0, top = 0, at, drift = 10, rotate = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame: frame - f(at), fps, config: { damping: 21, stiffness: 85, mass: 1.1 } });
  const d = interpolate(frame - f(at), [0, 210], [0, drift], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img
      src={staticFile(src)}
      style={{
        position: "absolute", left, top,
        ...(h ? { height: h } : {}), ...(w ? { width: w } : {}),
        opacity: s,
        filter: "drop-shadow(0 16px 20px rgba(80,50,20,0.26))",
        transform: `translateY(${(1 - s) * 46 - d}px) scale(${0.95 + s * 0.05}) rotate(${rotate}deg)`,
      }}
    />
  );
};

/** One word, landing exactly when it is spoken. */
const Word: React.FC<{
  idx: number; size: number; color?: string; weight?: number;
  strike?: boolean; underline?: boolean;
}> = ({ idx, size, color = INK, weight = 800, strike, underline }) => {
  const [start] = W[idx];
  const s = useWordSpring(start);
  const frame = useCurrentFrame();
  const grow = interpolate(frame, [f(start) + 3, f(start) + 13], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
  });
  return (
    <span
      style={{
        position: "relative", display: "inline-block",
        fontFamily: deva, fontWeight: weight, fontSize: size, lineHeight: 1.38,
        color, opacity: s, transform: `translateY(${(1 - s) * 20}px) scale(${0.9 + s * 0.1})`,
      }}
    >
      {W[idx][2]}
      {strike && (
        <span style={{ position: "absolute", left: -4, top: "53%", height: Math.max(4, size * 0.075), width: `calc((100% + 8px) * ${grow})`, background: KUMKUM, borderRadius: 4 }} />
      )}
      {underline && (
        <span style={{ position: "absolute", left: 0, bottom: -6, height: 4, width: `calc(100% * ${grow})`, background: GOLD, borderRadius: 3, opacity: 0.85 }} />
      )}
    </span>
  );
};

const Row: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 16 }) => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: `0 ${gap}px` }}>{children}</div>
);

/**
 * Beat window gated on the ABSOLUTE frame.
 * Deliberately NOT <Sequence> — Sequence rebases useCurrentFrame() to 0, which would break
 * every word spring in here (they are all keyed to absolute narration seconds).
 */
const Beat: React.FC<{ from: number; to: number; children: React.ReactNode }> = ({ from, to, children }) => {
  const frame = useCurrentFrame();
  if (frame < f(from) || frame >= f(to)) return null;
  const fade = interpolate(frame, [f(from), f(from) + 4, f(to) - 5, f(to)], [0, 1, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return <AbsoluteFill style={{ opacity: fade }}>{children}</AbsoluteFill>;
};

/* ══ BEAT A · 0.0–3.9 — the hook ═══════════════════════════════ */
const BeatA: React.FC = () => (
  <AbsoluteFill>
    <Cutout src="sanatan/manuscript.png" w={700} left={310} top={368} at={0.5} drift={14} rotate={-2} />
    <div style={{ position: "absolute", left: 0, right: 0, top: 132 }}>
      <Row gap={14}>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <Word key={i} idx={i} size={38} color={MUTED} weight={600} underline={i === 2} />
        ))}
      </Row>
      <div style={{ height: 16 }} />
      <Row>
        <Word idx={8} size={74} weight={900} />
        <Word idx={9} size={74} weight={900} />
        <Word idx={10} size={74} weight={900} color={KUMKUM} strike />
        <Word idx={11} size={74} weight={900} color={KUMKUM} />
        <Word idx={12} size={74} weight={900} color={KUMKUM} />
      </Row>
    </div>
  </AbsoluteFill>
);

/* ══ BEAT B · 3.9–5.8 — it is a RISHI's name ═══════════════════ */
const BeatB: React.FC = () => {
  const halo = useWordSpring(4.35, 20, 90);
  return (
    <AbsoluteFill>
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.55 }}>
        <circle cx={612} cy={250} r={128 + halo * 34} fill="none" stroke={GOLD} strokeWidth={2} />
        <circle cx={612} cy={250} r={96 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} />
      </svg>
      <Cutout src="sanatan/rishi.png" h={372} left={471} top={148} at={3.95} drift={7} />
      <div style={{ position: "absolute", left: 0, right: 0, top: 544, textAlign: "center" }}>
        <Row>
          <Word idx={15} size={72} weight={900} underline />
        </Row>
      </div>
    </AbsoluteFill>
  );
};

/* ══ BEAT C · 5.8–10.6 — the sutra ═════════════════════════════ */
const BeatC: React.FC = () => {
  const frame = useCurrentFrame();
  const Y = 396;
  const X0 = 372;
  const X1 = 928;
  // thread draws while "से … आप तक" is spoken
  const draw = interpolate(frame, [f(6.6), f(7.7)], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
  return (
    <AbsoluteFill>
      <Cutout src="sanatan/rishi.png" h={318} left={158} top={214} at={5.85} drift={6} />
      <Cutout src="sanatan/modern_man.png" h={336} left={952} top={200} at={7.1} drift={6} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        {/* sutra — a thread, deliberately not a chart axis */}
        <path
          d={`M${X0} ${Y} Q ${(X0 + X1) / 2} ${Y - 26} ${X0 + (X1 - X0) * draw} ${Y - 26 * Math.sin(Math.PI * draw)}`}
          fill="none" stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round"
        />
        {[0.25, 0.5, 0.75].map((t, i) => {
          const on = draw > t ? 1 : 0;
          const x = X0 + (X1 - X0) * t;
          const y = Y - 26 * Math.sin(Math.PI * t);
          return (
            <g key={i} opacity={on}>
              <circle cx={x} cy={y} r={7} fill={SAND} stroke={GOLD} strokeWidth={3} />
            </g>
          );
        })}
      </svg>
      <div style={{ position: "absolute", left: X0, width: X1 - X0, top: 452, textAlign: "center" }}>
        <Row gap={14}>
          <Word idx={26} size={50} weight={800} />
          <Word idx={27} size={50} weight={800} />
          <Word idx={28} size={62} weight={900} color={KUMKUM} underline />
        </Row>
      </div>
    </AbsoluteFill>
  );
};

/* ══ BEAT D+E · 10.6–15.8 — the shastra rule, in a mandir arch ══ */
const BeatDE: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const arch = spring({ frame: frame - f(11.0), fps, config: { damping: 22, stiffness: 95, mass: 1 } });
  const tag = useWordSpring(11.61, 16, 150);

  // cusped mandir arch
  const AW = 900, AH = 344, AX = 190, AY = 336;
  const archPath = `M0 ${AH} L0 ${AH * 0.52}
    Q ${AW * 0.06} ${AH * 0.30} ${AW * 0.20} ${AH * 0.22}
    Q ${AW * 0.34} ${AH * 0.13} ${AW * 0.42} 0
    Q ${AW * 0.5} ${AH * 0.10} ${AW * 0.58} 0
    Q ${AW * 0.66} ${AH * 0.13} ${AW * 0.80} ${AH * 0.22}
    Q ${AW * 0.94} ${AH * 0.30} ${AW} ${AH * 0.52}
    L ${AW} ${AH} Z`;

  return (
    <AbsoluteFill>
      <Cutout src="sanatan/couple.png" w={392} left={444} top={62} at={10.7} drift={7} />
      <svg width={AW} height={AH} style={{ position: "absolute", left: AX, top: AY, opacity: arch }}>
        <path d={archPath} fill="#FFFCF2" stroke={GOLD} strokeWidth={2.5}
          style={{ transform: `scaleY(${0.9 + arch * 0.1})`, transformOrigin: "bottom center" }} />
      </svg>

      <div style={{ position: "absolute", left: AX, width: AW, top: AY + 116, textAlign: "center" }}>
        <div
          style={{
            display: "inline-block", fontFamily: deva, fontSize: 22, fontWeight: 800,
            letterSpacing: 6, color: SAND, background: INK, padding: "6px 22px", borderRadius: 3,
            opacity: tag, transform: `translateY(${(1 - tag) * 10}px)`,
          }}
        >
          {W[34][2]}
        </div>
        <div style={{ height: 18 }} />
        <Row gap={18}>
          <Word idx={38} size={70} weight={900} />
          <Word idx={39} size={70} weight={900} />
          <Word idx={40} size={70} weight={900} color={KUMKUM} />
        </Row>
      </div>
    </AbsoluteFill>
  );
};

/* ══ BEAT F · 15.8–21 — the mechanism, then the word ═══════════ */
const BeatF: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const CX = 448, PY = 232, CY = 388;

  const pA = spring({ frame: frame - f(15.9), fps, config: { damping: 16, stiffness: 150 } });
  const pB = spring({ frame: frame - f(16.2), fps, config: { damping: 16, stiffness: 150 } });
  const link = interpolate(frame, [f(16.6), f(17.1)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const kid = spring({ frame: frame - f(17.05), fps, config: { damping: 14, stiffness: 160 } });
  const term = useWordSpring(17.43, 19, 110);

  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number }> = ({ x, s }) => (
    <g opacity={s} transform={`translate(${x},${PY}) scale(${0.86 + s * 0.14})`}>
      <circle r={44} fill="rgba(255,252,242,0.9)" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.24} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );

  return (
    <AbsoluteFill>
      <Cutout src="sanatan/dna.png" h={520} left={940} top={110} at={15.85} drift={11} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 128} s={pA} />
        <Carrier x={CX + 128} s={pB} />
        <line x1={CX - 110} y1={PY + 38} x2={CX - 110 + 110 * link} y2={PY + 38 + (CY - PY - 38) * link} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        <line x1={CX + 110} y1={PY + 38} x2={CX + 110 - 110 * link} y2={PY + 38 + (CY - PY - 38) * link} stroke={MARIGOLD} strokeWidth={4} strokeLinecap="round" />
        <g opacity={kid} transform={`translate(${CX},${CY}) scale(${0.84 + kid * 0.16})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{ position: "absolute", left: 84, top: 486, width: 730, textAlign: "center", opacity: term, transform: `translateY(${(1 - term) * 16}px)` }}>
        <div style={{ fontFamily: inter, fontSize: 42, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.8 }}>
          विज्ञान ने इसे एक नाम दिया।
        </div>
      </div>
    </AbsoluteFill>
  );
};

export const SanatanDecodeGotraV3: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: SAND }}>
    <Audio src={staticFile("sanatan/gotra.mp3")} />
    <Ground />
    <Mandala />
    <DiyaGlow />

    <Beat from={0} to={3.92}><BeatA /></Beat>
    <Beat from={3.92} to={5.82}><BeatB /></Beat>
    <Beat from={5.82} to={10.58}><BeatC /></Beat>
    <Beat from={10.58} to={15.76}><BeatDE /></Beat>
    <Beat from={15.76} to={21.05}><BeatF /></Beat>

    <Petals />
    <Furniture />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
